import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";

const ago = (iso) => {
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 90) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

/** Homepage banner cycling through the latest coins that passed all 7 trust checks. Polls every 60s. */
export default function VerifiedSpotlight({ className = "" }) {
  const [coins, setCoins] = useState([]);
  const [idx, setIdx] = useState(0);
  const [shown, setShown] = useState(true);

  useEffect(() => {
    const load = () => api.get("/spotlight/verified").then((r) => setCoins(r.data.coins || [])).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (coins.length < 2) return;
    const t = setInterval(() => {
      setShown(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % coins.length);
        setShown(true);
      }, 260);
    }, 6000);
    return () => clearInterval(t);
  }, [coins.length]);

  if (!coins.length) return null;
  const c = coins[Math.min(idx, coins.length - 1)];

  return (
    <div className={className} data-testid="verified-spotlight">
      <div className="border border-[#00ff41] bg-[#121212] px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <ShieldCheck size={18} className="text-[#00ff41] flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#00ff41] flex items-center gap-2">
              <span className="w-2 h-2 bg-[#00ff41] live-dot" /> Freshly verified
              {coins.length > 1 && <span className="text-[#d0d0d0]">· {idx + 1}/{coins.length}</span>}
            </div>
            <div
              className={`text-sm truncate transition-opacity duration-300 ${shown ? "opacity-100" : "opacity-0"}`}
              data-testid="spotlight-coin"
              data-slug={c.slug}
            >
              <Link to={`/c/${c.slug}`} data-testid="spotlight-coin-link" className="font-bold text-white hover:text-[#00ff41]">
                {c.name}
              </Link>
              <span className="text-[#e2e2e2]">
                {" "}· ${c.ticker} · passed all 7 trust checks
                {c.holders != null && ` · ${Number(c.holders).toLocaleString()} holders`} · {ago(c.verified_at)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {coins.length > 1 && (
            <div className="flex items-center gap-1.5" aria-label="spotlight pages">
              {coins.map((x, i) => (
                <button
                  key={x.slug}
                  onClick={() => {
                    setIdx(i);
                    setShown(true);
                  }}
                  aria-label={`Show ${x.name}`}
                  data-testid={`spotlight-dot-${i}`}
                  className={`w-2 h-2 transition-colors ${i === idx ? "bg-[#00ff41]" : "bg-[#3a3a3a] hover:bg-[#d0d0d0]"}`}
                />
              ))}
            </div>
          )}
          <Link
            to={`/c/${c.slug}`}
            data-testid="spotlight-cta"
            className="btn-brutal bg-[#00ff41] text-[#050505] font-bold uppercase tracking-wider text-[11px] px-4 py-2"
          >
            View coin →
          </Link>
          <Link
            to="/verified"
            data-testid="spotlight-all"
            className="font-mono text-[11px] uppercase tracking-widest text-[#e2e2e2] hover:text-[#00ff41]"
          >
            All verified
          </Link>
        </div>
      </div>
    </div>
  );
}
